import { apiRequest } from '@/shared/lib/api-client';

export type MessageEventAction = 'close' | 'keep_open' | 'queue' | 'assign_user' | 'no_ticket';

/**
 * Variável de template HSM — cada `{{N}}` no body/header.text, header media e
 * cada button URL com `{{N}}` recebe um desses.
 *
 * - `fixed`: texto literal definido pelo admin no editor
 * - `path`: caminho do JSON do payload (ex: "contact.name")
 * - `upload`: só pra header.media — usa o arquivo anexado no campo "Arquivo"
 */
export type MessageEventVariable =
  | { kind: 'fixed'; value: string; filename?: string }
  | { kind: 'path'; value: string; filename?: string }
  | { kind: 'upload'; filename?: string };

/**
 * Chaves possíveis em `template_variables`:
 *   - `body.1`, `body.2`, ... — variáveis do body
 *   - `header.text.1`, ... — variáveis de header tipo TEXT
 *   - `header.media` — URL/arquivo do header IMAGE/VIDEO/DOCUMENT
 *   - `button.0`, `button.1`, ... — URL de buttons URL com variável
 */
export type MessageEventVariables = Record<string, MessageEventVariable>;

export type MessageEventStatus =
  | 'pending'
  | 'processing'
  | 'sent'
  | 'failed'
  | 'invalid_payload'
  | 'channel_offline'
  | 'auth_failed';

export interface MessageEvent {
  id: number;
  public_id: string;
  name: string;
  channel_id: number;
  data_model: Record<string, unknown>;
  message_body: string | null;
  template_id: number | null;
  template_variables: MessageEventVariables;
  action_after: MessageEventAction;
  action_target_user_id: number | null;
  action_target_dept_id: number | null;
  lead_phone_path: string;
  lead_name_path: string | null;
  lead_media_path: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  trigger_path: string;
  /** URL absoluta com a origem DIRETA do servidor (API_PUBLIC_ORIGIN no env da
   *  API). Presente em prod: evita o hop do gateway Worker. Null em dev. */
  trigger_url?: string | null;
}

export interface CreateMessageEventInput {
  name: string;
  channel_id: number;
  data_model: Record<string, unknown>;
  message_body?: string | null;
  template_id?: number | null;
  template_variables?: MessageEventVariables;
  action_after: MessageEventAction;
  action_target_user_id?: number | null;
  action_target_dept_id?: number | null;
  lead_phone_path: string;
  lead_name_path?: string | null;
  lead_media_path?: string | null;
}

export type UpdateMessageEventInput = Partial<CreateMessageEventInput> & { active?: boolean };

export interface MessageEventExecution {
  id: number;
  event_id: number;
  raw_payload: unknown;
  resolved_phone: string | null;
  contact_id: number | null;
  ticket_id: number | null;
  message_id: number | null;
  action_applied: MessageEventAction | null;
  status: MessageEventStatus;
  error_code: string | null;
  error_detail: string | null;
  received_at: string;
  completed_at: string | null;
}

export interface BucketStats {
  total: number;
  sent: number;
  failed: number;
}

export function listMessageEvents(
  filters: { limit?: number; cursor?: number } = {},
): Promise<{ items: MessageEvent[]; next_cursor: number | null }> {
  const params = new URLSearchParams();
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.cursor) params.set('cursor', String(filters.cursor));
  const qs = params.toString();
  return apiRequest(`/message-events${qs ? `?${qs}` : ''}`);
}
export function getMessageEvent(id: number): Promise<MessageEvent> {
  return apiRequest(`/message-events/${id}`);
}
export function createMessageEvent(input: CreateMessageEventInput): Promise<{
  id: number;
  public_id: string;
  bearer_token: string;
  trigger_path: string;
  trigger_url?: string | null;
}> {
  return apiRequest('/message-events', { method: 'POST', body: input });
}
export function updateMessageEvent(id: number, input: UpdateMessageEventInput): Promise<void> {
  return apiRequest(`/message-events/${id}`, { method: 'PATCH', body: input });
}
export function deleteMessageEvent(id: number): Promise<void> {
  return apiRequest(`/message-events/${id}`, { method: 'DELETE' });
}
export function rotateMessageEventToken(id: number): Promise<{ bearer_token: string }> {
  return apiRequest(`/message-events/${id}/rotate-token`, { method: 'POST', body: {} });
}
export function listMessageEventExecutions(
  id: number,
  filters: { status?: MessageEventStatus; limit?: number; cursor?: number } = {},
): Promise<{ items: MessageEventExecution[]; next_cursor: number | null }> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.cursor) params.set('cursor', String(filters.cursor));
  const qs = params.toString();
  return apiRequest(`/message-events/${id}/executions${qs ? `?${qs}` : ''}`);
}
export function getMessageEventStats(id: number): Promise<Record<string, BucketStats>> {
  return apiRequest(`/message-events/${id}/stats`);
}

/**
 * Recursivo: extrai paths "folha" do JSON pra dropdown "Selecione uma variável".
 * Espelha `extractJsonPaths` do backend.
 */
export function extractJsonPaths(obj: unknown, prefix = ''): string[] {
  if (typeof obj !== 'object' || obj === null) return prefix ? [prefix] : [];
  if (Array.isArray(obj)) {
    return obj.flatMap((item, idx) =>
      extractJsonPaths(item, prefix ? `${prefix}.${idx}` : String(idx)),
    );
  }
  const entries = Object.entries(obj as Record<string, unknown>);
  return entries.flatMap(([key, value]) => {
    const next = prefix ? `${prefix}.${key}` : key;
    return extractJsonPaths(value, next);
  });
}

/**
 * Monta URL pública completa do trigger. Preferência: `trigger_url` do backend
 * (origem DIRETA do servidor, ex. https://srv01.chatplusbr.com — sem o hop do
 * gateway Worker). Fallback: origin atual da aba (dev, ou env ausente).
 */
export function buildTriggerUrl(triggerPath: string, triggerUrl?: string | null): string {
  if (triggerUrl) return triggerUrl;
  if (triggerPath.startsWith('http')) return triggerPath;
  return `${window.location.origin}${triggerPath}`;
}
